import {
  EmptyState,
  FlexBox,
  IconButton,
  InputText,
  SelectionList,
  SelectionListChangeEvent,
  Skeleton,
  Tooltip,
} from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import { DashboardFavorite } from 'lib-dashboard-types';
import { ampli } from 'lib-shared-tracking';
import { ChangeEvent, memo, useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';

import { dashboardApi } from 'apis/dashboard';
import { appActions } from 'app/app.actions';
import { useDashboardLandingPage } from 'app/views/layout/views/dashboard/utils/useDashboardLandingPage';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { sanitizeHtml } from 'utils/sanitizeHtml';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { useCurrentUser } from 'utils/user';

import { QueueDashboardFilterControlFavoritesItem } from './components/item/queueDashboardFilterControlFavoritesItem';
import itemStyles from './components/item/queueDashboardFilterControlFavoritesItem.module.scss';
import styles from './queueDashboardFilterControlFavorites.module.scss';

type QueueDashboardFilterControlFavoritesProps = {
  setKeepOpen: (keepOpen: boolean) => void;
};

export const QueueDashboardFilterControlFavorites = memo(
  function QueueDashboardFilterControlFavorites({
    setKeepOpen,
  }: QueueDashboardFilterControlFavoritesProps): JSX.Element {
    const dispatch = useAppDispatch();
    const notify = useNotifications();
    const { user } = useCurrentUser();
    const { params, replaceParams } = useAppSearchParams('/dashboard/queues');
    const { isLandingPage, setLandingPage, resetLandingPage } =
      useDashboardLandingPage();

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

    const { favorites, isLoading, isSuccess, isError, refetch } =
      dashboardApi.useReadDashboardFavoritesQuery(
        { dashboard: 'queue' },
        {
          selectFromResult: ({ data, ...rest }) => ({
            favorites: data?.data.favorites ?? [],
            ...rest,
          }),
        },
      );

    const filteredFavorites = useMemo((): readonly DashboardFavorite[] => {
      const minMatchCharLength = 2;

      if (debouncedSearchTerm.length < minMatchCharLength) {
        return favorites;
      }

      const fuse = new Fuse(favorites, {
        keys: ['name', 'description'],
        threshold: 0.2,
        minMatchCharLength,
        distance: 200,
      });

      return fuse.search(debouncedSearchTerm).map(({ item }) => item);
    }, [favorites, debouncedSearchTerm]);

    const [deleteFavorite] = dashboardApi.useDeleteDashboardFavoriteMutation();

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    };

    const handleClearSearch = () => {
      setSearchTerm('');
      setDebouncedSearchTerm('');
    };

    const handleChange = useCallback(
      ({ selectedItem }: SelectionListChangeEvent<string>) => {
        replaceParams({
          type: 'favourite',
          id: selectedItem.value,
        });

        setLandingPage({
          type: 'last_visited',
          url: {
            path: '/dashboard/queues',
            params: {
              search: {
                type: 'favourite',
                id: selectedItem.value,
              },
            },
          },
        });
      },
      [replaceParams, setLandingPage],
    );

    const handleCreate = useCallback(async () => {
      const { favorite: createdFavorite } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'dashboard:favorite',
            arg: {
              mode: 'create',
              params: {
                queue_ids: [],
                sort_by: params.sort_by,
              },
            },
          },
        }),
      );

      if (createdFavorite) {
        replaceParams({
          type: 'favourite',
          id: createdFavorite.id,
        });
      }
    }, [dispatch, params.sort_by, replaceParams]);

    const handleEdit = useCallback(
      async (favorite: DashboardFavorite) => {
        setKeepOpen(true);

        await dispatch(
          appActions.showModal({
            modal: {
              type: 'dashboard:favorite',
              arg: {
                mode: 'update',
                favorite,
              },
            },
          }),
        );

        setKeepOpen(false);
      },
      [dispatch, setKeepOpen],
    );

    const handleDelete = useCallback(
      async (favorite: DashboardFavorite) => {
        setKeepOpen(true);

        const { isConfirmed, confirm } = await dispatch(
          appActions.showModal({
            modal: {
              type: 'app:confirm',
              arg: {
                title: `Delete "${favorite.name}"`,
                description: 'Are you sure you want to delete this favorite?',
                confirm: {
                  variant: 'danger',
                  label: 'Delete',
                  async: true,
                  asyncLabel: 'Deleting...',
                },
              },
            },
          }),
        );

        if (!isConfirmed) {
          setKeepOpen(false);
          return;
        }

        confirm(async () => {
          try {
            await deleteFavorite({
              id: favorite.id,
              dashboard: favorite.dashboard,
            }).unwrap();

            ampli.dashboardFavoriteDeleted({ shared: favorite.shared });

            if (params.type === 'favourite' && params.id === favorite.id) {
              replaceParams({
                type: 'agent',
                sort_by: params.sort_by,
              });
            }

            notify(
              {
                title: 'Favorite deleted',
                text: sanitizeHtml(
                  `The favorite "${favorite.name}" has been deleted.`,
                  'plainText',
                ),
                icon: 'i-done',
                time: 3000,
              },
              NotificationType.Success,
            );

            // ampli.dashboardFavoriteDeleted();
          } catch (error) {
            notify(
              {
                title: 'Error while deleting the favorite.',
              },
              NotificationType.Error,
            );
          } finally {
            setKeepOpen(false);
          }
        });
      },
      [deleteFavorite, dispatch, notify, params, replaceParams, setKeepOpen],
    );

    const handleToggleLandingPage = useCallback(
      (favorite: DashboardFavorite) => {
        const isCurrentLandingPage = isLandingPage(favorite);

        if (isCurrentLandingPage) {
          resetLandingPage();

          ampli.dashboardFavoriteLandingPageUnset({
            shared: favorite.shared,
            isOwner: favorite.user.id === user.id,
          });
        } else {
          setLandingPage({
            type: 'user_preferred',
            url: {
              path: '/dashboard/queues',
              params: {
                search: {
                  type: 'favourite',
                  id: favorite.id,
                },
              },
            },
          });

          ampli.dashboardFavoriteLandingPageSet({
            shared: favorite.shared,
            isOwner: favorite.user.id === user.id,
          });
        }

        notify(
          {
            title: 'Success',
            text: sanitizeHtml(
              `"${favorite.name}" has been ${
                isCurrentLandingPage ? 'removed' : 'set'
              } as your landing page.`,
              'plainText',
            ),
            icon: 'i-done',
            time: 3000,
          },
          NotificationType.Success,
        );
      },
      [isLandingPage, notify, resetLandingPage, setLandingPage, user.id],
    );

    const handleTransferOwnership = useCallback(
      async (favorite: DashboardFavorite) => {
        setKeepOpen(true);

        await dispatch(
          appActions.showModal({
            modal: {
              type: 'dashboard:favorite',
              arg: {
                mode: 'transferOwnership',
                favorite,
              },
            },
          }),
        );

        setKeepOpen(false);
      },
      [dispatch, setKeepOpen],
    );

    const selectedValues = useMemo(
      () => (params.type === 'favourite' && params.id ? [params.id] : []),
      [params],
    );

    const noFavoritesMatch = debouncedSearchTerm && !filteredFavorites.length;
    const noFavoritesExist = !debouncedSearchTerm && !favorites.length;

    return (
      <>
        <FlexBox className={styles.sticky} padding="300" gap="200">
          <InputText
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search favorites..."
            suffix={{
              type: 'custom',
              value: searchTerm ? (
                <FlexBox alignItems="center" padding="0 100">
                  <IconButton
                    variant="tertiary"
                    icon="close"
                    onClick={handleClearSearch}
                  />
                </FlexBox>
              ) : null,
              noWrap: true,
            }}
          />

          <Tooltip content="New favorite list">
            <IconButton
              icon="plus"
              variant="secondary"
              size="large"
              onClick={handleCreate}
            />
          </Tooltip>
        </FlexBox>

        <SelectionList
          className={styles.list}
          selectedValues={selectedValues}
          onChange={handleChange}
          defaultActiveItemIndex={0}
        >
          {filteredFavorites.map((favorite) => (
            <QueueDashboardFilterControlFavoritesItem
              key={favorite.id}
              favorite={favorite}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleLandingPage={handleToggleLandingPage}
              onTransferOwnership={handleTransferOwnership}
            />
          ))}

          {isLoading &&
            Array.from({ length: 5 }).map((_, i) => (
              <SelectionList.Item
                key={i}
                value={i}
                className={itemStyles.item}
                noFixedSelectionStateSpace
                isDisabled
              >
                <Skeleton
                  height="16px"
                  style={{
                    width: `${50 + ((i * 37) % 50)}%`, // Randomizes skeleton widths
                  }}
                />
              </SelectionList.Item>
            ))}

          {isSuccess && noFavoritesMatch && (
            <FlexBox as="li" padding="300" justifyContent="center">
              <EmptyState
                icon="search"
                title="No favorites found"
                description="Try changing your search criteria."
                buttonProps={{
                  variant: 'secondary',
                  children: 'Clear search',
                  onClick: handleClearSearch,
                }}
              />
            </FlexBox>
          )}

          {isSuccess && noFavoritesExist && (
            <FlexBox as="li" padding="300" justifyContent="center">
              <EmptyState
                icon="star-full"
                title="No favorites created yet"
                description="Create favorite lists to quickly access your most important queues."
                buttonProps={{
                  variant: 'primary',
                  children: 'New favorite list',
                  onClick: handleCreate,
                }}
              />
            </FlexBox>
          )}

          {isError && noFavoritesExist && (
            <FlexBox as="li" padding="300" justifyContent="center">
              <EmptyState
                icon="exclamation-circle-filled"
                title="Error loading favorites"
                description="There was an unexpected error while loading your favorites."
                buttonProps={{
                  variant: 'secondary',
                  children: 'Try again',
                  onClick: refetch,
                }}
              />
            </FlexBox>
          )}
        </SelectionList>
      </>
    );
  },
);
